/**
 * QR-2, веб-срез В1: общие помощники msg-контроллеров.
 *
 * 🔴 Это НЕ вынос кода из `chatController` (§1.1 и §8 дизайна). Переписка 1:1
 * работает на бою с 25.08, трогать её в срезе В1 было запрещено — поэтому
 * помощники НОВЫЕ, и пользовался ими сперва только `roomController`.
 * 🆕 07.09 сделано одно осознанное исключение: `tofuVerdict` и
 * `trustBlocksSend` теперь общие для ОБОИХ контроллеров — правило доверия и
 * запрета отправки обязано быть одним (решение владельца о запрете при смене
 * ключа), две копии правила безопасности разошлись бы первой же правкой.
 * Остальные помощники 1:1 по-прежнему не трогает.
 *
 * Отсюда же граница отбора: сюда уходит только то, что НЕ знает предметной
 * области. Всё, что знает слово «визави» или слово «комната», живёт в своём
 * контроллере — общая функция с флагом `isRoom` вернула бы ветвление, от
 * которого мы и уходим.
 *
 * 🔴 Цена такой границы — ДВЕ копии `storePassphrase` и `storeNameFor`
 * (вторая в `chatController.js`). Разойдись они — и владелец получит новый
 * пустой Olm-стор, то есть «ключ изменился» у всех собеседников разом.
 * Поэтому совпадение пиняется тестом `msgRuntime.test.js`, а не обещанием.
 */
// Д-17: сторож «вышло обновление» узнаёт о (пере)подключении SSE отсюда —
// чистый модуль без React и без предметной области, цикл его не ждёт.
import { noteStreamReady } from '../utils/updateWatch';

/** Ключ passphrase в localStorage. КОНТРАКТ с `chatController.js` (там же). */
export const PASSPHRASE_KEY = 'msg_store_pass';

/** Первая пауза перед реконнектом (дальше удваивается). */
export const RECONNECT_BASE_MS = 1000;

/** Пауза реконнекта не растёт выше этого потолка. */
export const RECONNECT_MAX_MS = 30_000;

/** Разброс паузы: два контроллера не ломятся переподключаться в одну секунду. */
export const RECONNECT_JITTER_MS = 500;

/**
 * Бросается из шагов bootstrap после stop(): вызывающий глотает его молча —
 * это не провал крипты, а отмена (страница ушла со своего места).
 */
export class StoppedError extends Error {
  constructor(what = 'Контроллер') {
    super(`${what} остановлен`);
    this.name = 'StoppedError';
  }
}

/**
 * Passphrase IndexedDB-стора криптомашины. Живёт в localStorage рядом с did:
 * от XSS в том же origin это НЕ защита (политика MSG-5 — стор не должен быть
 * открытым: пароль отсекает чтение файла стора вне браузера и косые бэкапы
 * профиля). Потеря пары localStorage+IndexedDB = новое устройство.
 */
export function storePassphrase() {
  let pass = localStorage.getItem(PASSPHRASE_KEY);
  if (!pass) {
    const raw = new Uint8Array(32);
    globalThis.crypto.getRandomValues(raw);
    pass = Array.from(raw, (b) => b.toString(16).padStart(2, '0')).join('');
    localStorage.setItem(PASSPHRASE_KEY, pass);
  }
  return pass;
}

/**
 * Имя IndexedDB-стора криптомашины — по ПРИНЦИПАЛУ и устройству.
 *
 * 🔴 Форма обязана остаться прежней (`total-msg-o<id>-<did>`): смена имени =
 * новый пустой стор = новое устройство и «ключ изменился» у всех собеседников
 * разом. Префиксы — те же, что в matrixUserId и на Android
 * (PrincipalSession.PREFIXES): один словарь на все три места.
 */
export function storeNameFor(principal, did) {
  const prefix = { owner: 'o', employee: 'e', member: 'm' }[principal?.type];
  if (!prefix || !Number.isInteger(principal.id) || principal.id <= 0) {
    throw new Error(`не могу построить имя стора: ${JSON.stringify(principal)}`);
  }
  return `total-msg-${prefix}${principal.id}-${did}`;
}

/**
 * message_id сообщения. Рождается ОДИН РАЗ и переживает все повторы, поэтому
 * и стоит отдельной функцией: сервер дедупит именно по нему, а форма обязана
 * быть UUID (сервер канонизирует и отбивает всё остальное 422).
 *
 * `crypto.randomUUID` есть не везде (старый Safari, jsdom без webcrypto) —
 * запасной путь собирает тот же UUID v4 из `getRandomValues`.
 */
export function newMessageId() {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid;
  const raw = new Uint8Array(16);
  globalThis.crypto.getRandomValues(raw);
  raw[6] = (raw[6] & 0x0f) | 0x40; // версия 4
  raw[8] = (raw[8] & 0x3f) | 0x80; // вариант RFC 4122
  const hex = Array.from(raw, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}`
    + `-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Последовательная очередь операций с криптомашиной: OlmMachine не переносит
 * конкурентных вызовов (актор на Android, цепочка промисов тут).
 *
 * `isStopped` спрашивается ПЕРЕД каждым шагом: после остановки очередь не
 * пускает новую работу, а уже начатую вызывающий дожидается через drain().
 */
export class SerialQueue {
  constructor(isStopped = () => false) {
    this.isStopped = isStopped;
    this.tail = Promise.resolve();
  }

  enqueue(fn) {
    const next = this.tail.then(() => (this.isStopped() ? undefined : fn()));
    // Разрыв цепочки навсегда — деградация хуже пропуска: ошибку съедаем
    // здесь, а наружу её несёт сам fn.
    this.tail = next.catch(() => {});
    return next;
  }

  /** «Всё улеглось»: хвост очереди доехал. Никогда не реджектится. */
  drain() {
    return this.tail.then(() => undefined, () => undefined);
  }
}

/**
 * Цикл SSE-подписки: держит поток открытым, а на обрыве переподключается с
 * растущей паузой. Ровно то, без чего лента не живая: сообщения доезжают
 * только по перезагрузке страницы.
 *
 * 🔴 Предметной области здесь НЕТ намеренно (§1.1 дизайна). Какое уведомление
 * «наше» и что по нему делать, знает контроллер: в 1:1 это `conversation_id`,
 * в комнате — `room_id`. Общий цикл с флагом `isRoom` вернул бы ветвление, от
 * которого мы и уходим.
 *
 * Разбор отказов — тот же, что у 1:1 (`chatController._streamLoop`, инвариант
 * 4): 401 лечится «тычком» в REST (транспорт обновит токен) и реконнектом,
 * 403 терминален (нет прав — ждать нечего), всё остальное = обрыв связи.
 *
 * @param api        MsgApi: нужны openStream() и poke()
 * @param isStopped  «контроллер уже остановлен» — второй источник истины
 *                   помимо stop() (флаг у контроллера ставится раньше)
 * @param onNotify   СЫРОЙ разбор кадра; фильтрует вызывающий
 * @param onReady    подписка встала (и после каждого реконнекта)
 * @param onFatal    403: продолжать бессмысленно, цикл больше не поднимется
 */
export function startStreamLoop({
  api,
  isStopped = () => false,
  onNotify = () => {},
  onReady = () => {},
  onFatal = () => {},
  baseMs = RECONNECT_BASE_MS,
  maxMs = RECONNECT_MAX_MS,
  jitterMs = RECONNECT_JITTER_MS,
}) {
  let stopped = false;
  let stream = null;
  let sleepTimer = null;
  let wake = null;

  // Два источника, а не один: контроллер ставит свой флаг ДО того, как дойдёт
  // до нас, и цикл обязан замолчать уже тогда.
  const over = () => stopped || isStopped();

  // Отменяемая пауза: stop() будит её сразу и не оставляет живой таймер после
  // ухода со страницы.
  const sleep = (ms) => new Promise((resolve) => {
    wake = resolve;
    sleepTimer = setTimeout(resolve, ms);
  });

  const done = (async () => {
    let backoff = baseMs;
    while (!over()) {
      try {
        stream = api.openStream({ onNotify });
        await stream.ready;
        backoff = baseMs; // связь восстановилась — счётчик паузы с нуля
        // Д-17: после рестарта бэкенда (окно) поток встаёт заново — самый
        // ранний момент спросить, не сменился ли бандл. Ответа не ждём.
        noteStreamReady();
        onReady();
        await stream.closed;
      } catch (e) {
        if (over()) return;
        if (e?.status === 401) {
          // Токен протух: транспорт обновит его на любом REST-вызове
          try { await api.poke(); } catch { /* не встало — ниже реконнект */ }
        } else if (e?.status === 403) {
          onFatal(e);
          return;
        }
      }
      if (over()) return;
      await sleep(backoff + Math.floor(Math.random() * jitterMs));
      backoff = Math.min(backoff * 2, maxMs);
    }
  })();

  return {
    /** «Цикл доехал» — для тестов и для ожидания остановки. */
    done,
    stop() {
      stopped = true;
      clearTimeout(sleepTimer);
      wake?.();
      stream?.close();
    },
  };
}

/**
 * Низкоуровневый TOFU: приговор по паре «пин ↔ пришедший ключ».
 *
 * Знания о том, ЧЕЙ это ключ и что делать с приговором, здесь нет намеренно:
 * в 1:1 визави один, в комнате их до девяти, и доверие там живёт по КАЖДОЙ
 * паре (тип, id) отдельно.
 *
 * 🔴 «Ключ СМЕНИЛСЯ» — ЛИПКОЕ состояние (решение владельца 07.09: запрет
 * отправки до сверки кода). Пин перезаписывается новым ключом сразу — иначе
 * входящее с нового устройства ловило бы 410, — но факт смены живёт в пине
 * (`changedAt`) до явного решения человека. Без липкости приговор смывался
 * бы первым же пересмотром состава (он идёт перед КАЖДОЙ отправкой) или
 * перезагрузкой вкладки: предупреждение, которое исчезает само, — не запрет.
 * Снимает липкость только `verifiedAt` («Код сверен»); `ackedAt` («Отправить
 * всё равно») снимает ЗАПРЕТ, но не тревогу — плашка остаётся красной.
 *
 * @param known запиненное {ed25519, verifiedAt, changedAt?, ackedAt?} или null
 * @param ed25519 ключ из свежего бандла
 * @returns {status, changed, repin} — `changed` истинно только в момент
 *          ОБНАРУЖЕНИЯ смены (на него пишется системная строка), липкое
 *          состояние отдаёт `key_changed` с `changed: false`.
 */
export function tofuVerdict(known, ed25519) {
  if (!known) return { status: 'first_use', changed: false, repin: true };
  if (known.ed25519 !== ed25519) {
    return { status: 'key_changed', changed: true, repin: true };
  }
  if (known.changedAt && !known.verifiedAt) {
    return { status: 'key_changed', changed: false, repin: false };
  }
  return {
    status: known.verifiedAt ? 'verified' : 'unchanged',
    changed: false,
    repin: false,
  };
}

/**
 * МУ-1-1: сводка доверия по устройствам ОДНОГО человека.
 *
 * 🔴 Зачем. Пин доверия принадлежит устройству, а интерфейс говорит о человеке.
 * До МУ-1 это совпадало — у человека было одно устройство. Со связанными
 * устройствами их несколько, и сводка обязана быть ПЕССИМИСТИЧНОЙ: если хоть
 * одно устройство сменило ключ, человек остаётся подозрительным, даже когда
 * остальные в порядке. Иначе связанные устройства станут способом спрятать
 * подмену — ровно то, от чего защищает весь этот механизм.
 *
 * Возвращает запись в той же форме, что и раньше отдавалась наружу, плюс
 * `devices` — чтобы вызывающий знал, какому устройству что писать.
 *
 * `ed25519`/`did`/`deviceRowId` берём у того устройства, которое и определило
 * приговор: человек должен сверять числа именно с подозрительным устройством, а
 * не с любым из его.
 */
const TRUST_WORST_FIRST = ['key_changed', 'unknown', 'first_use', 'unchanged', 'verified'];

export function trustSummary(devices) {
  if (!Array.isArray(devices) || !devices.length) return null;
  const worst = TRUST_WORST_FIRST.find((st) => devices.some((d) => d.status === st))
    ?? devices[0].status;
  const lead = devices.find((d) => d.status === worst) ?? devices[0];
  return {
    status: worst,
    verified: devices.every((d) => d.verified === true),
    // Запрет снимается только когда подтверждено КАЖДОЕ сменившее ключ
    // устройство: подтверждение одного не имеет права говорить за другое.
    acked: devices.every((d) => d.status !== 'key_changed' || d.acked === true),
    ed25519: lead.ed25519 ?? null,
    did: lead.did ?? null,
    deviceRowId: lead.deviceRowId ?? null,
    devices,
  };
}

/**
 * Единственное правило запрета отправки — им пользуются ОБА контроллера
 * (правило одно, как счёт «тебя ждут» один с догоном: две копии разошлись бы).
 * `t` — элемент доверия из состояния экрана: {status, verified, acked}.
 */
export function trustBlocksSend(t) {
  return t?.status === 'key_changed' && !t.acked;
}
